/**
 * What a save would send, so the Save button can say it. Compares the saved
 * listing with the draft the user edited.
 */
import { GoogleListingItem, ListingOption, Translation } from 'channels/googleTTDService'

export interface ListingChanges {
    /** New listing not yet in aion. */
    created: boolean
    listingOn: boolean
    listingOff: boolean
    venue: boolean
    ticketsAdded: number
    ticketsRemoved: number
    ticketsChanged: number
}

const sameTranslations = (a: Translation[] = [], b: Translation[] = []) => {
    const clean = (list: Translation[]) =>
        list
            .filter((item) => item.text.trim())
            .map((item) => `${item.language}:${item.text}`)
            .sort()
            .join('|')
    return clean(a) === clean(b)
}

const sameOption = (a: ListingOption, b: ListingOption) =>
    !!a.baseTicket === !!b.baseTicket &&
    [...a.category].sort().join() === [...b.category].sort().join() &&
    sameTranslations(a.description, b.description)

export function listingChanges(saved: GoogleListingItem | undefined, draft: GoogleListingItem): ListingChanges {
    if (!saved) {
        return {
            created: true,
            listingOn: draft.includeInFeed,
            listingOff: false,
            venue: true,
            ticketsAdded: draft.options.length,
            ticketsRemoved: 0,
            ticketsChanged: 0,
        }
    }
    const savedByUuid = new Map(saved.options.map((option) => [option.articleUUID, option]))
    const draftByUuid = new Map(draft.options.map((option) => [option.articleUUID, option]))
    const venue =
        saved.googlePlaceId !== draft.googlePlaceId ||
        saved.locationId !== draft.locationId ||
        saved.image !== draft.image ||
        !sameTranslations(saved.description, draft.description)
    return {
        created: false,
        listingOn: !saved.includeInFeed && draft.includeInFeed,
        listingOff: saved.includeInFeed && !draft.includeInFeed,
        venue,
        ticketsAdded: draft.options.filter((option) => !savedByUuid.has(option.articleUUID)).length,
        ticketsRemoved: saved.options.filter((option) => !draftByUuid.has(option.articleUUID)).length,
        ticketsChanged: draft.options.filter((option) => {
            const before = savedByUuid.get(option.articleUUID)
            return before && !sameOption(before, option)
        }).length,
    }
}

export const hasChanges = (changes: ListingChanges): boolean =>
    changes.created ||
    changes.listingOn ||
    changes.listingOff ||
    changes.venue ||
    changes.ticketsAdded + changes.ticketsRemoved + changes.ticketsChanged > 0

/** Options in the draft that are not in the saved listing: they need the backoffice flag first. */
export const addedOptions = (saved: GoogleListingItem | undefined, draft: GoogleListingItem): ListingOption[] => {
    const savedUuids = new Set((saved?.options ?? []).map((option) => option.articleUUID))
    return draft.options.filter((option) => !savedUuids.has(option.articleUUID))
}

/** Options in the saved listing that the draft dropped: they lose the backoffice flag after the save. */
export const removedOptions = (saved: GoogleListingItem | undefined, draft: GoogleListingItem): ListingOption[] => {
    const draftUuids = new Set(draft.options.map((option) => option.articleUUID))
    return (saved?.options ?? []).filter((option) => !draftUuids.has(option.articleUUID))
}
